import type { ReactNode } from 'react';
import {
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Close } from '@mui/icons-material';

type DetailsModalContentPadding = 'normal' | 'none';
type DetailsModalContentOverflow = 'auto' | 'visible';

type DetailsModalShellProps = {
	open: boolean;
	onClose: () => void;
	ariaLabelledBy: string;
	closeLabel: string;
	title: ReactNode;
	subtitle?: ReactNode;
	icon?: ReactNode;
	children: ReactNode;
	footer?: ReactNode;
	direction?: 'rtl' | 'ltr';
	scroll?: 'paper' | 'body';
	contentPadding?: DetailsModalContentPadding;
	contentOverflow?: DetailsModalContentOverflow;
	actionsDivider?: boolean;
};

export const DetailsModalShell = ({
	open,
	onClose,
	ariaLabelledBy,
	closeLabel,
	title,
	subtitle,
	icon,
	children,
	footer,
	direction = 'rtl',
	scroll,
	contentPadding = 'normal',
	contentOverflow = 'auto',
	actionsDivider = true,
}: DetailsModalShellProps) => {
	const theme = useTheme();
	const isContentPadded = contentPadding === 'normal';

	const headerIconButtonSx = {
		minWidth: 40,
		minHeight: 40,
		color: 'text.secondary',
		borderRadius: 1,
		transition: 'transform 0.2s cubic-bezier(0.2, 0, 0, 1), background-color 0.2s ease',
		'&:active': { transform: 'scale(0.96)' },
		'&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			scroll={scroll}
			transitionDuration={{ exit: 0 }}
			aria-labelledby={ariaLabelledBy}
			PaperProps={{
				elevation: 0,
				sx: {
					WebkitFontSmoothing: 'antialiased',
					borderRadius: 2,
					border: '1px solid',
					borderColor: alpha(theme.palette.root.mediumGreen, 0.5),
					boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.1)}, 0 0 0 1px ${alpha(
						theme.palette.common.black,
						0.04
					)}`,
					...(contentOverflow === 'visible'
						? { maxHeight: 'none', overflow: 'visible' }
						: { overflow: 'hidden' }),
				},
			}}
			sx={
				contentOverflow === 'visible'
					? {
							'& .MuiDialogContent-root': {
								overflowY: 'visible',
							},
					  }
					: undefined
			}
		>
			<DialogTitle
				id={ariaLabelledBy}
				sx={{
					position: 'relative',
					pr: 0,
					pl: 0,
					pt: 0.5,
					pb: 1,
					pe: 6,
					direction,
				}}
			>
				<Stack direction="row" alignItems="flex-start" gap={1.5}>
					{icon}
					<Box sx={{ minWidth: 0 }}>
						<Typography
							variant="pageTitle"
							color={theme.palette.root.formTitle}
							component="span"
							sx={{ display: 'block', textWrap: 'balance', WebkitFontSmoothing: 'antialiased' }}
						>
							{title}
						</Typography>
						{subtitle ? (
							<Typography
								variant="body1"
								sx={{ mt: 0.75, display: 'block', color: 'text.secondary', textWrap: 'balance' }}
							>
								{subtitle}
							</Typography>
						) : null}
					</Box>
				</Stack>
				<IconButton
					onClick={onClose}
					sx={{
						...headerIconButtonSx,
						position: 'absolute',
						top: theme.spacing(0.25),
						insetInlineEnd: 0,
					}}
					aria-label={closeLabel}
				>
					<Close sx={{ fontSize: 20 }} />
				</IconButton>
			</DialogTitle>

			<DialogContent
				dividers
				sx={{
					direction,
					px: isContentPadded ? { xs: 2, sm: 2.5 } : 0,
					py: isContentPadded ? 2.5 : 0,
					overflow: contentOverflow,
					borderTopColor: alpha(theme.palette.root.basicDeeoGreen, 0.12),
					borderBottomColor: alpha(theme.palette.root.basicDeeoGreen, 0.08),
				}}
			>
				{children}
			</DialogContent>

			{footer ? (
				<DialogActions
					sx={{
						width: '100%',
						direction,
						px: { xs: 2, sm: 2.5 },
						py: 1.5,
						pt: 2,
						gap: 1,
						...(actionsDivider
							? {
									borderTop: '1px solid',
									borderColor: alpha(theme.palette.root.basicDeeoGreen, 0.08),
							  }
							: null),
					}}
				>
					{footer}
				</DialogActions>
			) : null}
		</Dialog>
	);
};
